import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchPeopleRoles,
  deletePeopleRole,
  getAllRolesWithCounts,
} from "@/api/peopleRoles";
import { PeopleRole } from "@/types";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Edit, Trash, Search, ArrowUpDown, Tag, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";

// Get color for role badge
const getRoleBadgeColor = (roleId: number): string => {
  // Special colors for specific roles
  if (roleId === 1) {
    // SuperAdmin - Red
    return "bg-red-500/10 text-red-500";
  }
  if (roleId === 5) {
    // Standard User - Blue
    return "bg-blue-500/10 text-blue-500";
  }

  // Define a set of vibrant transparent background and text color pairs
  const colorPairs = [
    "bg-primary/10 text-primary",
    "bg-blue-500/10 text-blue-500",
    "bg-amber-500/10 text-amber-500",
    "bg-green-500/10 text-green-500",
    "bg-purple-500/10 text-purple-500",
    "bg-rose-500/10 text-rose-500",
    "bg-teal-500/10 text-teal-500",
    "bg-orange-500/10 text-orange-500",
    "bg-indigo-500/10 text-indigo-500",
  ];

  return colorPairs[roleId % colorPairs.length];
};

const PeopleRolesList = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // State for search and sorting
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState<{
    key: keyof PeopleRole | "peopleCount";
    direction: "asc" | "desc";
  }>({
    key: "name",
    direction: "asc",
  });

  // State for delete confirmation
  const [deleteRoleItem, setDeleteRoleItem] = useState<PeopleRole | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Fetch people roles with counts
  const {
    data: rolesWithCounts = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["peopleRolesWithCounts"],
    queryFn: getAllRolesWithCounts,
  });

  // Handle sorting
  const handleSort = (column: keyof PeopleRole | "peopleCount") => {
    setSortConfig((prev) => ({
      key: column,
      direction:
        prev.key === column && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  // Filter and sort roles
  const filteredAndSortedRoles = [...rolesWithCounts]
    .filter((role) => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        role.name.toLowerCase().includes(query) ||
        (role.description && role.description.toLowerCase().includes(query))
      );
    })
    .sort((a, b) => {
      const direction = sortConfig.direction === "asc" ? 1 : -1;

      if (sortConfig.key === "peopleCount") {
        return direction === 1
          ? (a.peopleCount || 0) - (b.peopleCount || 0)
          : (b.peopleCount || 0) - (a.peopleCount || 0);
      }

      if (
        typeof a[sortConfig.key] === "string" &&
        typeof b[sortConfig.key] === "string"
      ) {
        return (
          direction *
          (a[sortConfig.key] as string).localeCompare(
            b[sortConfig.key] as string
          )
        );
      }

      return 0;
    });

  // Handle edit action
  const handleEditRole = (id: number) => {
    navigate(`/admin/system/people-roles/${id}/edit`);
  };

  // Handle delete action
  const handleDeleteRoleClick = (role: PeopleRole) => {
    setDeleteRoleItem(role);
    setConfirmDialogOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!deleteRoleItem) return;

    try {
      await deletePeopleRole(Number(deleteRoleItem.id));

      toast({
        title: "Role deleted",
        description: `${deleteRoleItem.name} role has been deleted successfully.`,
      });

      queryClient.invalidateQueries({ queryKey: ["peopleRolesWithCounts"] });
    } catch (error) {
      console.error("Error deleting role:", error);

      let errorMessage = "An error occurred while deleting the role";
      if (error instanceof Error) {
        errorMessage = error.message;
      }

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setConfirmDialogOpen(false);
      setDeleteRoleItem(null);
    }
  };

  const addRoleButton = (
    <Button
      onClick={() => navigate("/admin/system/people-roles/new")}
      className="whitespace-nowrap"
    >
      <Plus className="h-4 w-4 mr-2" />
      Add Role
    </Button>
  );

  return (
    <AdminLayout title="People Roles" actionButton={addRoleButton}>
      <div className="flex justify-between items-center mb-6">
        <Card className="flex-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Search and filter roles</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="relative flex-1 w-full">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search roles..."
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <div className="rounded-md border border-border">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-muted/50">
                <TableHead
                  className="w-[200px] cursor-pointer"
                  onClick={() => handleSort("name")}
                >
                  <div className="flex items-center space-x-1">
                    <span>Name</span>
                    {sortConfig.key === "name" && (
                      <ArrowUpDown
                        className={`h-4 w-4 ${
                          sortConfig.direction === "asc"
                            ? "transform rotate-180"
                            : ""
                        }`}
                      />
                    )}
                  </div>
                </TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Status</TableHead>
                <TableHead
                  className="cursor-pointer"
                  onClick={() => handleSort("peopleCount")}
                >
                  <div className="flex items-center space-x-1">
                    <span>People Count</span>
                    {sortConfig.key === "peopleCount" && (
                      <ArrowUpDown
                        className={`h-4 w-4 ${
                          sortConfig.direction === "asc"
                            ? "transform rotate-180"
                            : ""
                        }`}
                      />
                    )}
                  </div>
                </TableHead>
                <TableHead className="w-[100px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="h-24 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="h-24 text-center text-destructive"
                  >
                    Error loading roles
                  </TableCell>
                </TableRow>
              ) : filteredAndSortedRoles.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="h-24 text-center text-muted-foreground"
                  >
                    <div className="flex flex-col items-center justify-center">
                      <Tag className="h-8 w-8 mb-2 text-muted-foreground/50" />
                      <h3 className="font-medium">No roles found</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        {searchQuery
                          ? `No roles match "${searchQuery}"`
                          : "There are no roles defined yet."}
                      </p>
                      {searchQuery && (
                        <Button
                          variant="outline"
                          onClick={() => setSearchQuery("")}
                          className="mt-2"
                          size="sm"
                        >
                          Clear Search
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredAndSortedRoles.map((role) => (
                  <TableRow key={role.id} className="hover:bg-muted/50">
                    <TableCell>
                      <Badge
                        className={cn(
                          "font-medium px-2 py-1 border-transparent",
                          getRoleBadgeColor(Number(role.id))
                        )}
                      >
                        {role.name}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {role.description || (
                        <span className="text-muted-foreground text-sm">
                          No description
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={role.isActive ? "default" : "outline"}>
                        {role.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {role.peopleCount || 0} people
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleEditRole(Number(role.id))}
                        >
                          <Edit className="h-4 w-4" />
                          <span className="sr-only">Edit</span>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleDeleteRoleClick(role)}
                        >
                          <Trash className="h-4 w-4" />
                          <span className="sr-only">Delete</span>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Role</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete{" "}
              <span className="font-medium">{deleteRoleItem?.name}</span>? This
              action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default PeopleRolesList;
